import type { Coordinate2D, Coordinate3D, PoleFormatOption } from '../types';

/**
 * Calculate 2D distance between two points
 */
export function calculate2DDistance(point1: Coordinate2D, point2: Coordinate2D): number {
  return Math.sqrt(
    Math.pow(point2.x - point1.x, 2) + 
    Math.pow(point2.y - point1.y, 2)
  );
}

/**
 * Calculate 3D distance between world coordinates
 */
export function calculate3DDistance(point1: Coordinate3D, point2: Coordinate3D): number {
  return Math.sqrt(
    Math.pow(point2.x - point1.x, 2) + 
    Math.pow(point2.y - point1.y, 2) + 
    Math.pow(point2.z - point1.z, 2)
  );
}

/**
 * Format pole measurement string based on format option
 */
export function formatPoleMeasurementString(
  height: number,
  poleHeight: number,
  format: PoleFormatOption
): string {
  switch (format) {
    case 'percent':
      const percent = ((poleHeight - height) / poleHeight) * 100;
      return `${percent.toFixed(1)}%`;
    
    case 'distance':
      return `${height.toFixed(2)}ft`;
    
    case 'both':
      const pct = ((poleHeight - height) / poleHeight) * 100;
      return `${height.toFixed(2)}ft (${pct.toFixed(1)}%)`;
    
    case 'none':
    default:
      return '';
  }
}

/**
 * Calculate attachment height from pixel position
 */
export function calculateAttachmentHeight(
  pixelY: number,
  topPoint: { pixel: Coordinate2D; world: Coordinate3D },
  bottomPoint: { pixel: Coordinate2D; world: Coordinate3D }
): number {
  // Calculate the ratio of the pixel position between top and bottom
  const pixelRatio = (pixelY - topPoint.pixel.y) / 
                     (bottomPoint.pixel.y - topPoint.pixel.y);
  
  // Apply the same ratio to world coordinates
  const worldHeight = topPoint.world.z - 
                     (pixelRatio * (topPoint.world.z - bottomPoint.world.z));
  
  return worldHeight;
}

/**
 * Calculate X position for a given Y on a line
 */
export function calculateXforY(
  y: number,
  bottomPoint: Coordinate2D,
  topPoint: Coordinate2D
): number {
  const dx = topPoint.x - bottomPoint.x;
  const dy = topPoint.y - bottomPoint.y;
  const EPSILON = 1e-6;
  if (Math.abs(dx) < EPSILON) {
    // Line is vertical or near-vertical; x is constant along the line
    return bottomPoint.x;
  }
  const slope = dy / dx;
  const yIntercept = bottomPoint.y - slope * bottomPoint.x;
  return (y - yIntercept) / slope;
}

/**
 * Calculate bearing between two geographic coordinates
 */
export function calculateBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;
  
  const x = Math.sin(dLon) * Math.cos(lat2Rad);
  const y = Math.cos(lat1Rad) * Math.sin(lat2Rad) - 
           Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);
  
  let bearing = Math.atan2(x, y) * 180 / Math.PI;
  
  // Normalize to 0-360 degrees
  return (bearing + 360) % 360;
}

/**
 * Calculate distance between two geographic coordinates (Haversine formula)
 */
export function calculateGeoDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;
  
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
           Math.cos(φ1) * Math.cos(φ2) *
           Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c; // Distance in meters
}

/**
 * Measurement calculation results
 */
export interface MeasurementResults {
  height: number;
  percentFromTop: number;
  distanceFromTop: number;
  distanceFromBottom: number;
  formattedString: string;
}

/**
 * Calculate comprehensive measurements for an attachment
 */
export function calculateMeasurements(
  pixelY: number,
  topPoint: { pixel: Coordinate2D; world: Coordinate3D },
  bottomPoint: { pixel: Coordinate2D; world: Coordinate3D },
  poleHeight: number,
  format: PoleFormatOption
): MeasurementResults {
  const height = calculateAttachmentHeight(pixelY, topPoint, bottomPoint);
  const distanceFromTop = poleHeight - height;
  const distanceFromBottom = height;
  const percentFromTop = (distanceFromTop / poleHeight) * 100;
  
  return {
    height,
    percentFromTop,
    distanceFromTop,
    distanceFromBottom,
    formattedString: formatPoleMeasurementString(height, poleHeight, format),
  };
}